import React, { useEffect } from 'react';
import {
  Form,
  FormItem,
  SubmitButton,
  Button,
  Input,
  Slider,
  Toggle,
  AutoComplete,
  FileReader,
} from '../../../../components';
import { sliderStepOptions } from '../const';
import BulkUploadPreview from './BulkUploadPreview';
import { validateDomains } from '../../../../components/AgentForm/utils/validators';
import type { CompanyInfo } from './BulkUploadPreview';
import { validatePageLimit } from '../utils/validators';

export type FormProps = {
  is_include_links?: boolean;
  depth?: number | null;
  take_screenshot?: boolean;
  url: string;
  hard_parse?: boolean;
  organisation_name?: string;
  agent_id?: string;
  exclude_path_regex?: string;
  include_path_regex?: string;
  campaign_name?: string;
  should_create_demo_bot?: boolean;
  companies_info?: CompanyInfo[];
  page_limit?: number | string;
};

type TaskFormProps = {
  saving: boolean;
  agentsOptions: OptionType[];
  formValues: FormProps;
  formId: string;
  setFormValues: (values: FormProps) => void;
  bulkUploadData: CompanyInfo[];
  handleBulkUpload: (rows: Record<string, string>[] | null) => void;
  handleClearBulkUpload: () => void;
  onCancel: () => void;
  onSave: () => void;
};

const TaskForm: React.FC<TaskFormProps> = ({
  saving,
  onSave,
  formId,
  onCancel,
  agentsOptions,
  formValues,
  setFormValues,
  bulkUploadData,
  handleBulkUpload,
  handleClearBulkUpload,
}) => {
  const { should_create_demo_bot } = formValues;

  useEffect(() => {
    if (bulkUploadData.length > 0 && !should_create_demo_bot) {
      handleClearBulkUpload();
    }
  }, [bulkUploadData, should_create_demo_bot, handleClearBulkUpload]);

  return (
    <Form
      onChange={setFormValues}
      initialValue={formValues}
      key={formId || 'new'}
    >
      <div className="flex flex-col w-full px-4 h-full">
        <div className="flex flex-col gap-y-6 border border-gray-200 p-6 rounded-sm bg-gray-50 overflow-y-auto">
          <div className="flex flex-col gap-y-6 border border-gray-200 p-6 rounded-sm bg-gray-50">
            <FormItem<FormProps>
              name="url"
              label="Website URL"
              inline={false}
              required={bulkUploadData.length === 0}
              validate={validateDomains(['http', 'https'])}
            >
              <Input placeholder="URL" disabled={bulkUploadData.length > 0} />
            </FormItem>

            <div className="flex gap-x-4">
              <FormItem<FormProps>
                name="exclude_path_regex"
                label="Exclude path regex"
                inline={false}
              >
                <Input />
              </FormItem>
              <FormItem<FormProps>
                name="include_path_regex"
                label="Include path regex"
                inline={false}
              >
                <Input />
              </FormItem>
            </div>

            <FormItem<FormProps> name="depth" label="" inline={false}>
              <Slider min={0} max={6} stepOptions={sliderStepOptions} />
            </FormItem>
          </div>

          <div className="flex flex-col gap-y-6 border border-gray-200 p-6 rounded-sm bg-gray-50">
            <FormItem<FormProps>
              name="page_limit"
              label="Number of pages to scrape"
              inline={false}
              validate={validatePageLimit}
              required
            >
              <Input placeholder="Enter page limit" />
            </FormItem>
          </div>

          <div className="grid grid-cols-2 gap-x-8 gap-y-8 items-end border border-gray-200 p-6 rounded-sm bg-gray-50">
            <FormItem<FormProps>
              name="is_include_links"
              label="Output"
              inline={false}
            >
              <Toggle disabled={false} label="Include links" />
            </FormItem>
            <FormItem<FormProps>
              name="take_screenshot"
              label="Screenshot"
              inline={false}
            >
              <Toggle disabled={false} label="Take screenshot" />
            </FormItem>
            <FormItem<FormProps> name="hard_parse" label="" inline={false}>
              <Toggle disabled={false} label="Hard parse" />
            </FormItem>
            <FormItem<FormProps>
              name="should_create_demo_bot"
              label=""
              inline={false}
            >
              <Toggle disabled={false} label="Assign Demo Agent" />
            </FormItem>
          </div>

          {should_create_demo_bot && (
            <div className="flex flex-col gap-y-6 border border-gray-200 p-6 rounded-sm bg-gray-50">
              <div className="flex gap-x-4 col-span-2 items-end">
                <FormItem<FormProps>
                  name="agent_id"
                  label="Select Agent"
                  inline={false}
                  required={should_create_demo_bot}
                  className="flex-1"
                >
                  <AutoComplete options={agentsOptions || []} />
                </FormItem>
                <FileReader
                  onChange={handleBulkUpload}
                  viewMode="upload-button"
                  uploadButtonText="Bulk Upload"
                  requiredColumns={[
                    'company_name',
                    'organisation_name',
                    'url',
                    'website',
                  ]}
                  uploadButtonClassName="bg-select text-main border border-main px-4 py-4 rounded-md text-small"
                  className="p-0 rounded-md self-end"
                />
              </div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-4 col-span-2">
                <FormItem<FormProps>
                  name="organisation_name"
                  label="Company Name"
                  inline={false}
                  required={
                    should_create_demo_bot && bulkUploadData.length === 0
                  }
                >
                  <Input
                    placeholder="Company Name"
                    disabled={bulkUploadData.length > 0}
                  />
                </FormItem>
                <FormItem<FormProps>
                  name="campaign_name"
                  label="Campaign Name"
                  inline={false}
                  required={should_create_demo_bot}
                >
                  <Input placeholder="Campaign Name" />
                </FormItem>
              </div>
            </div>
          )}
        </div>
        {bulkUploadData.length > 0 && (
          <div className="mt-6">
            <BulkUploadPreview
              companies={bulkUploadData}
              onClear={handleClearBulkUpload}
            />
          </div>
        )}

        <div className="flex gap-x-4 justify-end items-end mt-6">
          <Button
            size="large"
            type="contrast"
            label="Cancel"
            className="text-main"
            onClick={onCancel}
          />
          <SubmitButton
            size="large"
            disabled={saving}
            className=""
            label="Scrape"
            onClick={onSave}
          />
        </div>
      </div>
    </Form>
  );
};

export default TaskForm;
